import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { NATIVE_OR_SIM } from '../lib/proximity/native/simPhone';
import { App } from '@capacitor/app';
import { supabase } from '../supabaseClient';
import {
  createNativePositionPublisher,
  syncNativePublishTokens,
} from '../lib/proximity/data/nativePublishPosition';
import {
  createCapacitorBleBridge,
  createCapacitorLocationBridge,
  createPeerProfilesModule,
  createPositionSubscriber,
  createProximityEngine,
  onBleGpsPeersChange,
  onRemoteGpsPeersChange,
} from '../lib/proximity/runtime';
import { clearRemoteGpsPeers } from '../lib/proximity/engine/peerGpsStore';
import { onFindPeersRefreshRequest } from '../lib/proximity/data/findPeersRefresh';
import { fetchBeaconKeys, getBeaconMode } from '../lib/proximity/beaconPrivacy';
import { useBeaconMode } from './useBeaconMode';
import { readFindPeerSnapshot, writeFindPeerSnapshot } from '../lib/proximity/findPeerSnapshot';
import {
  resolveFindPeerDisplayName,
  resolveFindPeerPhotoUrl,
} from '../lib/proximity/findPeerDisplayName';
import { resetFindDistanceHysteresis } from '../lib/proximity/runtime';
import {
  findRemoteLog,
  startFindRemoteLogSession,
  stopFindRemoteLogSession,
  updateFindRemoteLogGroup,
} from '../lib/findRemoteLog';
import { useManagedSessionRunKey, waitForSessionMinLifetime } from './useManagedSessionRunKey';
import { consumeUserGroupSelectionArm } from '../config/findSessionGuard';
import { SOLO_SESSION_GROUP } from './useManagedSessionRunKey';
import { FIND_PEERS_SNAPSHOT_HEARTBEAT_MS } from '../config/findRemoteLogging';
import { parseSessionRunKey } from '../lib/proximity/sessionRunKeyInstrumentation';

import { LynxLocation } from '../lib/proximity/native/LynxLocation';

const IS_NATIVE = NATIVE_OR_SIM;
const nativeBleBridge = IS_NATIVE ? createCapacitorBleBridge() : null;

/** How often buffered native BLE/UWB events are pulled into lynx_find_logs while JS runs. */
const NATIVE_EVENT_DRAIN_MS = 5_000;

/**
 * Files the native BLE/UWB lifecycle events (scan seen, token sent/received, NI run/invalidated) Swift buffered
 * since the last drain. None of these reached the logs before, which left the 2026-10-14 airplane-mode test
 * undiagnosable. Each keeps its native time in `at`; client_ts is when JS got it (after a resume, maybe minutes later).
 */
async function drainNativeBleEvents() {
  if (!nativeBleBridge?.drainNativeEvents) {
    return;
  }
  let result;
  try {
    result = await nativeBleBridge.drainNativeEvents();
  } catch {
    return; // a native build without the method
  }
  for (const entry of result?.events ?? []) {
    const { event, ...payload } = entry ?? {};
    if (typeof event !== 'string' || !event) {
      continue;
    }
    findRemoteLog('info', `find.native.${event}`, payload, {
      skipThrottle: true,
      source: 'native',
    });
  }
  if (result?.dropped > 0) {
    findRemoteLog(
      'warn',
      'find.native.dropped',
      { count: result.dropped },
      { skipThrottle: true, source: 'native' },
    );
  }
}

async function disposeSessionInstance(session) {
  if (!session || session.disposed) {
    return;
  }
  session.disposed = true;

  if (session.refreshTimerId !== null) {
    clearTimeout(session.refreshTimerId);
    session.refreshTimerId = null;
  }
  if (session.allowEmptyPeersTimerId !== null) {
    clearTimeout(session.allowEmptyPeersTimerId);
    session.allowEmptyPeersTimerId = null;
  }
  if (session.heartbeatTimerId !== null) {
    clearInterval(session.heartbeatTimerId);
    session.heartbeatTimerId = null;
  }

  session.removeListeners?.();

  session.publisher?.stop();
  await session.subscriber?.stop();
  session.engine?.stopTracking();
  await session.engine?.stop();

  session.selfPositionUnsub?.();
  session.selfPositionUnsub = null;
  session.engine?.getDeviceHeading()?.dispose();
}

/**
 * React bridge to the proximity engine + M3 data layer.
 * Starts native BLE/GPS/subscribe/publish only on a physical device when groupId is set.
 */
export function useProximity({
  userId,
  groupId,
  enabled = true,
  selfProfile = null,
  findPageActive = true,
  /** No group, but an event that is on: publish your own position and watch the friends you follow back. */
  solo = false,
  /** The session's event: the group's, or the live RSVP's when solo. Mutual follows going to it are watched. */
  eventId = null,
  /** When that event stops being live (epoch ms); handed to the native publisher, which stops itself past it. */
  liveUntilMs = null,
  /** Every group you have for the session's event; their members are all watched (no picking, 2026-10-12). */
  groupIds = null,
  /** { lat, lng, radiusM } of the session's event, for the native off-site rule; null for none. */
  venue = null,
}) {
  // A ref, not a dependency: the end of the event never restarts a running session, it rides along when one starts.
  const liveUntilRef = useRef(liveUntilMs);
  liveUntilRef.current = liveUntilMs;
  const venueRef = useRef(venue);
  venueRef.current = venue;
  const queryClient = useQueryClient();
  const [engineStatus, setEngineStatus] = useState('idle');
  const [subscriptionStatus, setSubscriptionStatus] = useState('idle');
  const [publishStatus, setPublishStatus] = useState(null);
  const [peers, setPeers] = useState([]);
  const [peersHydration, setPeersHydration] = useState('none');
  const [startError, setStartError] = useState(null);
  const [locationAuth, setLocationAuth] = useState('unknown');
  const [locationAuthLost, setLocationAuthLost] = useState(false);
  /** 'full' | 'reduced' (Precise Location off) | null before native has said, or on an older native build. */
  const [locationAccuracy, setLocationAccuracy] = useState(null);
  const [backgroundLocationEnabled, setBackgroundLocationEnabled] = useState(false);
  const [headingNeedsTap, setHeadingNeedsTap] = useState(false);
  const [headingDenied, setHeadingDenied] = useState(false);
  const [deviceHeadingDeg, setDeviceHeadingDeg] = useState(null);
  const [compassReady, setCompassReady] = useState(false);
  const [bleState, setBleState] = useState('unknown');
  const [trackedPeerId, setTrackedPeerId] = useState(null);
  // The battery saver (LynxBatterySaver, 2026-10-23) stands down while you track someone; the Find log notes it turning on
  // and off, so an event audit can see when updates slowed.
  useEffect(() => {
    if (!IS_NATIVE) return;
    LynxLocation.setTracking({ on: Boolean(trackedPeerId) }).catch(() => {});
  }, [trackedPeerId]);
  useEffect(() => {
    if (!IS_NATIVE) return undefined;
    let handle = null;
    let gone = false;
    LynxLocation.addListener('batterySaverChanged', (e) =>
      findRemoteLog('info', 'find.battery_saver', e, { skipThrottle: true, source: 'native' }),
    )
      .then((h) => {
        if (gone) h.remove();
        else handle = h;
      })
      .catch(() => {});
    return () => {
      gone = true;
      handle?.remove();
    };
  }, []);
  const [trackedUwbReading, setTrackedUwbReading] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [livePeersSynced, setLivePeersSynced] = useState(false);

  const engineRef = useRef(null);
  const publisherRef = useRef(null);
  const subscriberRef = useRef(null);
  const profilesRef = useRef(null);
  const locationBridgeRef = useRef(null);
  const selfPositionUnsubRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const persistContextRef = useRef({ userId: null, groupId: null });
  const allowEmptyPeersRef = useRef(false);
  const allowEmptyPeersTimerRef = useRef(null);
  const activeSessionRef = useRef(null);
  const sessionLifecycleRef = useRef(Promise.resolve());
  const selfProfileRef = useRef(selfProfile);
  const queryClientRef = useRef(queryClient);
  const bleStateRef = useRef('unknown');
  const displayPeersRef = useRef([]);
  const peersHydrationRef = useRef('none');
  const prevHydratedGroupRef = useRef(null);
  const findPageActiveRef = useRef(findPageActive);

  selfProfileRef.current = selfProfile;
  queryClientRef.current = queryClient;
  persistContextRef.current = { userId, groupId };
  displayPeersRef.current = peers;
  peersHydrationRef.current = peersHydration;
  findPageActiveRef.current = findPageActive;

  // Settings' Find testing card changes the beacon mode: the session restarts with it (the default adds nothing).
  const beaconModeLive = useBeaconMode();
  const { managedSessionRunKey, managedSessionRunKeyRef } = useManagedSessionRunKey({
    enabled,
    userId,
    groupId,
    solo,
    eventId,
    variant: beaconModeLive === 'shadow' ? null : `beacon-${beaconModeLive}`,
  });

  /**
   * sessionRunKey only changes when user/group change, so a session torn down by
   * backgrounding or the nav grace timer could never come back. Bumping the epoch
   * re-runs the session effect with the same key.
   */
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const sessionEpochRef = useRef(0);
  /** Set when a stop was our own doing (background / nav grace) so it can be undone. */
  const needsRestartRef = useRef(false);
  const requestSessionRestart = useCallback((reason) => {
    if (!IS_NATIVE || !needsRestartRef.current) {
      return;
    }
    const active = activeSessionRef.current;
    if (active && !active.disposed) {
      return;
    }
    needsRestartRef.current = false;
    findRemoteLog('info', 'find.session.restart_requested', { reason }, { skipThrottle: true });
    sessionEpochRef.current += 1;
    setSessionEpoch(sessionEpochRef.current);
  }, []);
  const requestSessionRestartRef = useRef(requestSessionRestart);
  requestSessionRestartRef.current = requestSessionRestart;

  const runContextRef = useRef({ userId, groupId, eventId, groupIds });
  runContextRef.current = { userId, groupId, eventId, groupIds };
  // Joined or left a group for this event mid-session: widen or narrow who is watched without a restart.
  const groupIdsKey = (groupIds ?? []).join(',');
  useEffect(() => {
    void subscriberRef.current?.setGroupIds?.(groupIdsKey ? groupIdsKey.split(',') : []);
  }, [groupIdsKey]);

  const prevGroupIdRef = useRef(groupId);
  useEffect(() => {
    if (prevGroupIdRef.current !== groupId && prevGroupIdRef.current != null && groupId != null) {
      resetFindDistanceHysteresis();
      updateFindRemoteLogGroup(groupId);
    }
    prevGroupIdRef.current = groupId;
  }, [groupId]);

  const resolvePeerIdentity = useCallback((peerId, sessionDisplayName, sessionPhotoUrl) => {
    const { userId: viewerUserId } = persistContextRef.current;
    return {
      displayName: resolveFindPeerDisplayName({
        peerId,
        queryClient: queryClientRef.current,
        viewerUserId,
        selfProfile: selfProfileRef.current,
        sessionDisplayName,
      }),
      photoUrl: resolveFindPeerPhotoUrl({
        peerId,
        queryClient: queryClientRef.current,
        viewerUserId,
        selfProfile: selfProfileRef.current,
        sessionPhotoUrl,
      }),
    };
  }, []);

  const getSelfPosition = useCallback(
    () => engineRef.current?.getSelfPositionModule()?.getSelfPosition() ?? null,
    [],
  );

  const getMergedGpsPeers = useCallback(() => engineRef.current?.getMergedGpsPeers() ?? [], []);

  const refreshPeers = useCallback(
    ({ snapshotSource = 'event' } = {}) => {
      const engine = engineRef.current;
      if (!engine) {
        return;
      }

      const rows = engine.getSortedPeersWithDisplay().map(({ peer, display }) => {
        const profiles = profilesRef.current;
        const sessionDisplayName =
          profiles?.getDisplayName(peer.peerId) ?? display.displayName ?? null;
        const sessionPhotoUrl = profiles?.getPhotoUrl(peer.peerId) ?? null;
        const { displayName, photoUrl } = resolvePeerIdentity(
          peer.peerId,
          sessionDisplayName,
          sessionPhotoUrl,
        );
        // The display resolves the bearing with the distance: UWB live or held through a blink, else smoothed GPS.
        const bearingDeg = display.bearingDeg;
        const hasMeaningfulBearing = bearingDeg != null && Number.isFinite(bearingDeg);

        return {
          peerId: peer.peerId,
          displayName,
          photoUrl,
          display,
          peer,
          bearingDeg: hasMeaningfulBearing ? bearingDeg : null,
          uwbActive: peer.uwbActive === true,
          uwbDistance:
            peer.uwbDistance != null && Number.isFinite(peer.uwbDistance) ? peer.uwbDistance : null,
          uwbDirectionAvailable: peer.uwbDirectionAvailable === true,
        };
      });

      setPeers((current) => {
        const subscriberStatus = subscriberRef.current?.getStatus() ?? 'idle';
        const mayAcceptEmptyLive =
          allowEmptyPeersRef.current &&
          (subscriberStatus === 'connected' || subscriberStatus === 'error');
        if (rows.length === 0 && current.length > 0 && !mayAcceptEmptyLive) {
          return current;
        }
        return rows;
      });

      const subscriberStatus = subscriberRef.current?.getStatus() ?? 'idle';
      const acceptedLiveRows = rows.length > 0;
      const acceptedEmptyLive =
        rows.length === 0 &&
        allowEmptyPeersRef.current &&
        (subscriberStatus === 'connected' || subscriberStatus === 'error');
      if (acceptedLiveRows || acceptedEmptyLive) {
        setLivePeersSynced(true);
        setPeersHydration('live');
      }
      setEngineStatus(engine.status);
      setPublishStatus(publisherRef.current?.getStatus() ?? null);
      setSubscriptionStatus(subscriberRef.current?.getStatus() ?? 'idle');
      setTrackedUwbReading(engine.getTrackedUwbReading());
      setTrackedPeerId((current) => {
        const tracked = engine.getTrackedUserId();
        return tracked ?? (current && rows.some((row) => row.peerId === current) ? current : null);
      });

      const snapshotPayload = {
        snapshotSource,
        peerCount: rows.length,
        displayPeerCount: displayPeersRef.current.length,
        peersHydration: peersHydrationRef.current,
        engineStatus: engine.status,
        publishStatus: publisherRef.current?.getStatus() ?? null,
        subscriptionStatus: subscriberRef.current?.getStatus() ?? 'idle',
        trackedPeerId: engine.getTrackedUserId(),
        kinds: rows.reduce((acc, row) => {
          const kind = row.display?.kind ?? 'unknown';
          acc[kind] = (acc[kind] ?? 0) + 1;
          return acc;
        }, {}),
        // What each friend's row SAID: the 2026-10-12 audit could tell "live" from "stale" but not whether "20 ft" was
        // right, because the distance shown was never logged. id is the whole key the row is filed under: eight
        // characters could not tell an alias from its person (2026-10-14 Turnstile, one friend shown twice).
        rosterSize: engine.getRosterIds()?.length ?? null,
        registryKeys: [...engine.getPeers().keys()].slice(0, 12),
        gpsIds: engine
          .getMergedGpsPeers()
          .slice(0, 12)
          .map((p) => `${p.source}:${p.id}`),
        peers: rows.slice(0, 12).map((row) => ({
          id: String(row.peerId ?? ''),
          idLen: String(row.peerId ?? '').length,
          kind: row.display?.kind ?? null,
          m: Number.isFinite(row.display?.distanceMeters)
            ? Math.round(row.display.distanceMeters * 10) / 10
            : null,
          acc: Number.isFinite(row.display?.horizontalAccuracyMeters)
            ? Math.round(row.display.horizontalAccuracyMeters)
            : null,
          uwb: Number.isFinite(row.uwbDistance) ? Math.round(row.uwbDistance * 100) / 100 : null,
        })),
        // Carried on every snapshot, not just on transition: a compass that never
        // changes state produces no transition log at all, which is exactly the hole
        // that made the Red Rocks arrow problem undiagnosable after the fact.
        compass: engine.getDeviceHeading()?.getDiagnostics?.() ?? null,
      };

      findRemoteLog(
        snapshotSource === 'heartbeat' ? 'info' : 'debug',
        'proximity.peers.snapshot',
        snapshotPayload,
        { throttleKey: 'proximity.peers.snapshot' },
      );

      const selfPosition = engine.getSelfPositionModule()?.getSelfPosition();
      if (selfPosition) {
        findRemoteLog(
          'debug',
          'proximity.gps.self_fix',
          {
            lat: selfPosition.lat,
            lng: selfPosition.lng,
            accuracyMeters: selfPosition.accuracyMeters ?? null,
          },
          { throttleKey: 'proximity.gps.self_fix' },
        );
      }

      const { userId: uid, groupId: gid } = persistContextRef.current;
      if (uid && gid && (rows.length > 0 || allowEmptyPeersRef.current)) {
        writeFindPeerSnapshot(uid, gid, rows);
      }
    },
    [resolvePeerIdentity],
  );

  const scheduleRefreshRef = useRef(() => {});
  scheduleRefreshRef.current = () => {
    if (refreshTimerRef.current !== null) {
      return;
    }
    refreshTimerRef.current = window.setTimeout(() => {
      refreshTimerRef.current = null;
      refreshPeers();
    }, 0);
  };

  const clearGlobalSessionRefs = useCallback((session) => {
    if (engineRef.current === session?.engine) {
      engineRef.current = null;
    }
    if (publisherRef.current === session?.publisher) {
      publisherRef.current = null;
    }
    if (subscriberRef.current === session?.subscriber) {
      subscriberRef.current = null;
    }
    if (profilesRef.current === session?.profiles) {
      profilesRef.current = null;
    }
    if (locationBridgeRef.current === session?.locationBridge) {
      locationBridgeRef.current = null;
    }
    if (selfPositionUnsubRef.current === session?.selfPositionUnsub) {
      selfPositionUnsubRef.current = null;
    }
  }, []);

  const resetUiAfterSessionStop = useCallback(() => {
    setIsRunning(false);
    setEngineStatus('idle');
    setSubscriptionStatus('idle');
    setPublishStatus(null);
    setTrackedPeerId(null);
    setTrackedUwbReading(null);
    setDeviceHeadingDeg(null);
    setCompassReady(false);
  }, []);

  const stopActiveSession = useCallback(async () => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (allowEmptyPeersTimerRef.current !== null) {
      clearTimeout(allowEmptyPeersTimerRef.current);
      allowEmptyPeersTimerRef.current = null;
    }
    allowEmptyPeersRef.current = false;

    const session = activeSessionRef.current;
    activeSessionRef.current = null;
    if (!session) {
      resetUiAfterSessionStop();
      await stopFindRemoteLogSession();
      return;
    }

    await disposeSessionInstance(session);
    clearGlobalSessionRefs(session);
    resetUiAfterSessionStop();
    await stopFindRemoteLogSession();
  }, [clearGlobalSessionRefs, resetUiAfterSessionStop]);

  const refreshLocationAuth = useCallback(async (bridge) => {
    const locationBridge = bridge ?? locationBridgeRef.current;
    if (!locationBridge) {
      return;
    }
    try {
      const { status, accuracy } = await locationBridge.getAuthorizationStatus();
      setLocationAuth(status);
      if (accuracy) setLocationAccuracy(accuracy);
      if (status === 'whenInUse' || status === 'always') {
        setLocationAuthLost(false);
      }
      return status;
    } catch {
      setLocationAuth('unknown');
      return 'unknown';
    }
  }, []);

  /**
   * CoreLocation reports transient failures (kCLErrorLocationUnknown) through the same
   * channel as denial. Only tear down publishing when authorization is actually gone.
   */
  const handleLocationAuthLost = useCallback(
    async (message) => {
      const status = await refreshLocationAuth(locationBridgeRef.current);
      const authGone = status === 'denied' || status === 'restricted' || status === 'notDetermined';
      if (!authGone) {
        findRemoteLog(
          'warn',
          'proximity.gps.error',
          { message: message ?? null, status: status ?? null },
          { throttleKey: 'proximity.gps.error' },
        );
        return;
      }
      findRemoteLog(
        'warn',
        'proximity.gps.auth_lost',
        { message: message ?? null, status: status ?? null },
        { skipThrottle: true },
      );
      publisherRef.current?.stop();
      setLocationAuthLost(true);
    },
    [refreshLocationAuth],
  );
  const handleLocationAuthLostRef = useRef(handleLocationAuthLost);
  handleLocationAuthLostRef.current = handleLocationAuthLost;

  const requestLocationWhenInUse = useCallback(async () => {
    // No live session (a quiet day): ask through a bridge of its own. Before, "Turn on" returned here and did nothing.
    const bridge =
      locationBridgeRef.current ?? (IS_NATIVE ? createCapacitorLocationBridge() : null);
    if (!bridge) {
      return;
    }
    try {
      const { status, accuracy } = await bridge.requestWhenInUseAuthorization();
      setLocationAuth(status);
      if (accuracy) setLocationAccuracy(accuracy);
    } catch {
      setLocationAuth('denied');
    }
  }, []);

  const openAppSettings = useCallback(async () => {
    const bridge = locationBridgeRef.current;
    if (!bridge) {
      return false;
    }
    try {
      const { opened } = await bridge.openAppSettings();
      return opened === true;
    } catch {
      return false;
    }
  }, []);

  /**
   * Ask iOS to upgrade When-In-Use to Always. Returns the bridge status so the caller can
   * tell "prompt shown" (`pending`) from "nothing happened" (any concrete status).
   *
   * Without Always, `allowsBackgroundLocationUpdates` stays false in LynxLocationEngine,
   * iOS suspends the web view on background, and the publish timer stops with it — which
   * is exactly what happened at the Rose Bowl on 2026-09-05: 3h16m of Find with 14 fixes,
   * all of them in the seconds the app happened to be on screen.
   *
   * `pending` is deliberately not written to locationAuth: it is a transient "asked, no
   * answer yet" marker, and it overlaps locationNeedsRequest, which would flip the UI back
   * to the initial-grant state while the system alert is up. The real answer arrives on
   * the authorizationChanged listener.
   */
  const requestLocationAlways = useCallback(async () => {
    const bridge = locationBridgeRef.current;
    if (!bridge) {
      return 'unknown';
    }
    findRemoteLog('info', 'proximity.gps.always_requested', {}, { skipThrottle: true });
    try {
      const { status, accuracy } = await bridge.requestAlwaysAuthorization();
      if (accuracy) setLocationAccuracy(accuracy);
      if (status !== 'pending') {
        setLocationAuth(status);
      }
      return status;
    } catch {
      return 'unknown';
    }
  }, []);

  const requestCompassPermission = useCallback(async () => {
    const heading = engineRef.current?.getDeviceHeading();
    if (!heading) {
      return false;
    }
    const granted = await heading.requestPermission();
    setHeadingNeedsTap(heading.needsTap());
    setHeadingDenied(heading.isPermissionDenied());
    return granted;
  }, []);

  useLayoutEffect(() => {
    if (!userId || !groupId) {
      prevHydratedGroupRef.current = null;
      setPeers([]);
      setPeersHydration('none');
      setLivePeersSynced(false);
      return;
    }

    const previousGroupId = prevHydratedGroupRef.current;
    if (previousGroupId && previousGroupId !== groupId && displayPeersRef.current.length > 0) {
      writeFindPeerSnapshot(userId, previousGroupId, displayPeersRef.current);
    }
    prevHydratedGroupRef.current = groupId;

    const cached = readFindPeerSnapshot(userId, groupId);
    setPeers(cached);
    setPeersHydration(cached.length > 0 ? 'snapshot' : 'none');
    setLivePeersSynced(false);
    findRemoteLog(
      'debug',
      'find.peers.hydrate',
      {
        groupId,
        cachedCount: cached.length,
        kinds: cached.reduce((acc, row) => {
          const kind = row.display?.kind ?? 'unknown';
          acc[kind] = (acc[kind] ?? 0) + 1;
          return acc;
        }, {}),
      },
      { skipThrottle: true },
    );
  }, [userId, groupId]);

  useEffect(() => {
    if (!nativeBleBridge) {
      setBleState('unsupported');
      return undefined;
    }

    let cancelled = false;
    let removeListener = () => {};

    void (async () => {
      try {
        const { state } = await nativeBleBridge.getBluetoothState();
        if (!cancelled) {
          setBleState(state);
        }
      } catch {
        if (!cancelled) {
          setBleState('unknown');
        }
      }
      removeListener = await nativeBleBridge.addBluetoothStateListener((state) => {
        if (!cancelled) {
          if (bleStateRef.current !== state) {
            findRemoteLog(
              'info',
              'proximity.ble.state',
              {
                from: bleStateRef.current,
                to: state,
              },
              { throttleKey: 'proximity.ble.state' },
            );
            bleStateRef.current = state;
          }
          setBleState(state);
        }
      });
    })();

    return () => {
      cancelled = true;
      void removeListener();
    };
  }, []);

  /**
   * Authorization is otherwise only read inside a live session, so with no session the
   * permission chip reads "unknown" (shield) even when access is granted. This watches
   * it independently and reacts to grants made in Settings.
   *
   * Always, not only while Find is enabled (Sam, 2026-10-19): on a day with nothing on Find is off, the status was
   * never read, and Maps showed Location as off with a "Turn on" that could do nothing. Reading the status and
   * listening for changes is not using location - the no-location-on-quiet-days rule (10-12) still holds.
   */
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  useEffect(() => {
    if (!IS_NATIVE) {
      return undefined;
    }

    const bridge = createCapacitorLocationBridge();
    let cancelled = false;
    let unsubscribe = null;

    /**
     * Whether CoreLocation will keep delivering with the app backgrounded. Read from the
     * plugin rather than inferred from the authorization string: the rule lives in
     * LynxLocationEngine.canRunInBackground(), and duplicating it here is how the UI ends
     * up confidently telling the user the opposite of what the OS is doing.
     */
    const refreshBackgroundStatus = async () => {
      try {
        const status = await bridge.getBackgroundLocationStatus();
        if (!cancelled) {
          setBackgroundLocationEnabled(status.backgroundEnabled === true);
        }
      } catch {
        if (!cancelled) {
          setBackgroundLocationEnabled(false);
        }
      }
    };

    const applyStatus = (status, accuracy) => {
      if (cancelled) {
        return;
      }
      setLocationAuth(status);
      if (accuracy) setLocationAccuracy(accuracy);
      if (status === 'whenInUse' || status === 'always') {
        setLocationAuthLost(false);
      }
      void refreshBackgroundStatus();
    };

    void (async () => {
      try {
        const { status, accuracy } = await bridge.getAuthorizationStatus();
        applyStatus(status, accuracy);
      } catch {
        if (!cancelled) {
          setLocationAuth('unknown');
        }
      }

      const handle = await bridge.addAuthorizationChangedListener(({ status, accuracy }) => {
        if (cancelled) {
          return;
        }
        findRemoteLog(
          'info',
          'proximity.gps.auth_changed',
          { status, accuracy: accuracy ?? null },
          { skipThrottle: true },
        );
        applyStatus(status, accuracy);
        if ((status === 'whenInUse' || status === 'always') && enabledRef.current) {
          requestSessionRestartRef.current('location_granted');
        }
      });

      if (cancelled) {
        void handle();
        return;
      }
      unsubscribe = handle;
    })();

    return () => {
      cancelled = true;
      void unsubscribe?.();
    };
  }, []);

  /**
   * Swift cannot see the web layer's auth state, and a festival day outlasts an access
   * token. Swift refreshes itself after a 401, but that spends a failed publish first —
   * this keeps it ahead of expiry instead.
   */
  useEffect(() => {
    if (!IS_NATIVE || !enabled) {
      return undefined;
    }
    const bridge = createCapacitorLocationBridge();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.access_token) {
        return;
      }
      void syncNativePublishTokens(bridge, {
        accessToken: session.access_token,
        refreshToken: session.refresh_token ?? null,
      });
    });
    return () => {
      subscription?.unsubscribe();
    };
  }, [enabled]);

  const trackPeer = useCallback((peerId) => {
    const engine = engineRef.current;
    if (!engine) {
      return;
    }
    engine.trackUser(peerId);
    // The engine resolves a truncated Bluetooth id to the person's full id; the screens use that one.
    const tracked = engine.getTrackedUserId() ?? peerId;
    setTrackedPeerId(tracked);
    findRemoteLog(
      'info',
      'proximity.track.start',
      { peerId: tracked, ...(tracked !== peerId ? { requested: peerId } : {}) },
      { skipThrottle: true },
    );
    scheduleRefreshRef.current();
  }, []);

  const untrackPeer = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) {
      setTrackedPeerId(null);
      return;
    }
    const wasTracked = engine.getTrackedUserId();
    engine.stopTracking();
    setTrackedPeerId(null);
    setTrackedUwbReading(null);
    if (wasTracked) {
      findRemoteLog('info', 'proximity.track.stop', { peerId: wasTracked }, { skipThrottle: true });
    }
    scheduleRefreshRef.current();
  }, []);

  const toggleTrackPeer = useCallback(
    (peerId) => {
      if (trackedPeerId === peerId) {
        untrackPeer();
      } else {
        trackPeer(peerId);
      }
    },
    [trackedPeerId, trackPeer, untrackPeer],
  );

  const stopActiveSessionRef = useRef(stopActiveSession);
  stopActiveSessionRef.current = stopActiveSession;
  const clearGlobalSessionRefsRef = useRef(clearGlobalSessionRefs);
  clearGlobalSessionRefsRef.current = clearGlobalSessionRefs;
  const resetUiAfterSessionStopRef = useRef(resetUiAfterSessionStop);
  resetUiAfterSessionStopRef.current = resetUiAfterSessionStop;
  const refreshLocationAuthRef = useRef(refreshLocationAuth);
  refreshLocationAuthRef.current = refreshLocationAuth;

  useEffect(() => {
    if (!IS_NATIVE || !enabled || !isRunning) {
      return undefined;
    }
    void drainNativeBleEvents();
    const id = setInterval(() => {
      if (document.hidden) return; // native buffers them; pause/resume drain (an idle web view, 2026-10-29)
      void drainNativeBleEvents();
    }, NATIVE_EVENT_DRAIN_MS);
    return () => {
      clearInterval(id);
      void drainNativeBleEvents();
    };
  }, [enabled, isRunning]);

  /**
   * Leaving Find no longer ends the session.
   *
   * The nav grace timer used to tear the whole session down 120 s after you navigated
   * away, so being findable meant standing on the Find page with the app in the
   * foreground. That is not what the feature is for: your group needs to see you while
   * you are reading set times or texting them about where to meet.
   *
   * The split is the same one backgrounding already uses — pause the expensive radios,
   * keep the cheap publisher alive. The high-duty RSSI scan and our own UWB ranging are
   * what cost battery, so they still pause off Find. Advertising and the GATT responder
   * (userId, GPS, UWB token) do not: they are how a friend with no internet finds you
   * (Turnstile test, 2026-10-14), so they run app-wide, in the background too, for as long
   * as the session does.
   */
  const pauseRadiosOffFind = useCallback(() => {
    const session = activeSessionRef.current;
    if (!session || session.disposed) {
      return;
    }
    if (session.engine?.status === 'running' && !session.blePaused) {
      void session.engine.pauseBle().then(() => {
        if (activeSessionRef.current !== session) {
          return;
        }
        session.blePaused = true;
        findRemoteLog(
          'debug',
          'find.session.ble_paused',
          { reason: 'off_find' },
          {
            skipThrottle: true,
          },
        );
      });
    }
  }, []);

  useEffect(() => {
    if (!IS_NATIVE || !enabled) {
      return undefined;
    }

    const session = activeSessionRef.current;

    // The session runs app-wide now, so it is started here whether or not Find is the
    // current route — the radar just happens to be the page that renders it.
    if (!session || session.disposed) {
      requestSessionRestartRef.current(findPageActive ? 'find_page_active' : 'app_active');
      return undefined;
    }

    if (findPageActive) {
      if (session.blePaused && session.engine?.status === 'running') {
        void session.engine.resumeBle().then(() => {
          if (activeSessionRef.current !== session) {
            return;
          }
          session.blePaused = false;
          findRemoteLog('debug', 'find.session.ble_resumed', {}, { skipThrottle: true });
          scheduleRefreshRef.current();
        });
      }
      return undefined;
    }

    pauseRadiosOffFind();

    return undefined;
  }, [findPageActive, enabled, pauseRadiosOffFind]);

  /**
   * `appStateChange isActive:false` fires on willResignActive — a permission alert,
   * Control Center or a notification banner all trigger it. Only `pause`
   * (didEnterBackground) means the app is really gone, and `resume` brings it back.
   */
  useEffect(() => {
    if (!IS_NATIVE) {
      return undefined;
    }

    let removed = false;
    const handles = [];

    const addListener = (eventName, handler) => {
      void App.addListener(eventName, handler).then((handle) => {
        if (removed) {
          void handle.remove();
          return;
        }
        handles.push(handle);
      });
    };

    /**
     * Backgrounding used to tear the whole session down and cold-restart on resume. At
     * Red Rocks that fired 12 times in 65 minutes — every lock screen, notification or
     * Control Center pull — and each restart re-acquired GPS from 100–200 m, re-subscribed
     * realtime and re-paired UWB. Suspend the expensive radios instead and keep the
     * engine, publisher state and peer set alive so resume is instant.
     */
    addListener('pause', () => {
      if (removed) {
        return;
      }
      const session = activeSessionRef.current;
      if (!session || session.disposed) {
        findRemoteLog(
          'info',
          'find.session.background_stop',
          { suspended: false },
          { skipThrottle: true },
        );
        needsRestartRef.current = true;
        void stopActiveSessionRef.current();
        return;
      }

      findRemoteLog(
        'info',
        'find.session.background_suspend',
        { suspended: true },
        { skipThrottle: true },
      );
      void drainNativeBleEvents();
      session.backgroundSuspended = true;
      if (session.engine?.status === 'running' && !session.blePaused) {
        void session.engine
          .pauseBle()
          .then(() => {
            if (activeSessionRef.current === session) {
              session.blePaused = true;
            }
          })
          .catch((err) => {
            // A failed suspend must not leave a half-live session — fall back to the
            // old teardown so resume rebuilds from scratch.
            findRemoteLog(
              'warn',
              'find.session.suspend_failed',
              { message: err instanceof Error ? err.message : String(err) },
              { skipThrottle: true },
            );
            needsRestartRef.current = true;
            void stopActiveSessionRef.current();
          });
      }
    });

    addListener('resume', () => {
      if (removed) {
        return;
      }
      const session = activeSessionRef.current;
      const alive = Boolean(session && !session.disposed);
      // What the radios did while JS was suspended: the part of an offline test that matters most.
      void drainNativeBleEvents();
      findRemoteLog(
        'info',
        'find.session.foreground_resume',
        {
          hasSession: alive,
          wasSuspended: Boolean(session?.backgroundSuspended),
          findPageActive: findPageActiveRef.current,
        },
        { skipThrottle: true },
      );

      if (!alive) {
        requestSessionRestartRef.current('foreground');
        return;
      }

      session.backgroundSuspended = false;
      void (async () => {
        try {
          // Radios come back only for the page that uses them; the publisher and the
          // subscription below are what keep you findable everywhere else.
          if (
            findPageActiveRef.current &&
            session.engine?.status === 'running' &&
            session.blePaused
          ) {
            await session.engine.resumeBle();
            if (activeSessionRef.current === session) {
              session.blePaused = false;
            }
          }
          // The websocket rarely survives suspension; recover it in place.
          await session.subscriber?.resync();
        } catch (err) {
          findRemoteLog(
            'warn',
            'find.session.resume_failed',
            { message: err instanceof Error ? err.message : String(err) },
            { skipThrottle: true },
          );
          requestSessionRestartRef.current('resume_failed');
        }
      })();
    });

    return () => {
      removed = true;
      for (const handle of handles) {
        void handle.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (engineRef.current) {
      scheduleRefreshRef.current();
    }
  }, [selfProfile]);

  useEffect(() => {
    const keyForEffect = managedSessionRunKey;
    let cancelled = false;
    let localSession = null;

    const run = async () => {
      if (cancelled) {
        return;
      }

      const targetGroupId = parseSessionRunKey(keyForEffect).groupId;
      const userIntent = targetGroupId != null && consumeUserGroupSelectionArm(targetGroupId);

      const active = activeSessionRef.current;
      if (keyForEffect && active?.key === keyForEffect && active.startedAt && !active.disposed) {
        scheduleRefreshRef.current();
        return;
      }

      const supersededDuringWait = await waitForSessionMinLifetime(
        activeSessionRef.current,
        keyForEffect,
        managedSessionRunKeyRef,
        { userIntent },
      );
      if (cancelled || supersededDuringWait) {
        return;
      }

      await stopActiveSessionRef.current();
      if (cancelled) {
        return;
      }

      if (!keyForEffect) {
        setLivePeersSynced(false);
        setStartError(null);
        return;
      }

      const { userId: uid, groupId: gid, eventId: eid, groupIds: gids } = runContextRef.current;
      setStartError(null);
      setLivePeersSynced(false);
      clearRemoteGpsPeers();

      const locationBridge = createCapacitorLocationBridge();
      const profiles = createPeerProfilesModule(supabase);
      const resolveEngineName = (peerId) =>
        resolveFindPeerDisplayName({
          peerId,
          queryClient: queryClientRef.current,
          viewerUserId: uid,
          selfProfile: selfProfileRef.current,
          sessionDisplayName: profiles.getDisplayName(peerId),
        });

      // 'shadow' by default: the legacy beacon as it is, the private one checked beside it, logging only
      // (docs/beacon-privacy.md). 'on' replaces the legacy beacon - only once the shadow logs say it works.
      const beaconMode = getBeaconMode();
      const engine = createProximityEngine({
        localPeerId: uid,
        beaconPrivacy: beaconMode === 'on',
        beaconShadow: beaconMode === 'shadow',
        // The beacon stops at the event's end natively, and a relaunch will not restore it past it (2026-10-16 audit).
        liveUntilMs: liveUntilRef.current,
        locationBridge,
        enableSelfPosition: true,
        enableDeviceHeading: true,
        bleBridge: nativeBleBridge ?? createCapacitorBleBridge(),
        nameResolver: resolveEngineName,
        onEvent: () => scheduleRefreshRef.current(),
        onLocationError: (event) => {
          void handleLocationAuthLostRef.current(event.message);
        },
      });

      /**
       * Publishing runs in Swift, not here.
       *
       * JS cannot be trusted to tick in the background: without a background mode iOS
       * suspends the whole app (measured 2026-09-06, zero JS events across a 5m24s lock),
       * and WITH the location mode the web view runs but must stay idle (2026-10-29: a busy
       * hidden web view got the web process replaced on the next open). createNativePositionPublisher
       * implements the same interface, so the logging, status and lifecycle below are untouched.
       */
      const publisher = createNativePositionPublisher({
        bridge: locationBridge,
        supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
        anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        userId: uid,
        getTokens: async () => {
          const { data } = await supabase.auth.getSession();
          return {
            accessToken: data?.session?.access_token ?? null,
            refreshToken: data?.session?.refresh_token ?? null,
          };
        },
        onEvent: (event) => {
          if (event.type === 'error') {
            findRemoteLog(
              'error',
              'proximity.publish.error',
              { code: event.code, message: event.message, groupId: gid },
              { throttleKey: 'proximity.publish.error' },
            );
            return;
          }
          if (event.type === 'token_refreshed') {
            // The only evidence the 401 refresh-and-retry path ever runs. Without it a
            // long soak looks the same whether Swift refreshed successfully or the token
            // simply had not expired yet.
            findRemoteLog(
              'info',
              'proximity.publish.token_refreshed',
              { groupId: gid },
              { skipThrottle: true },
            );
            return;
          }
          if (event.type === 'no_fix') {
            // The first publish tick routinely lands before CoreLocation's first
            // callback. Only a fix that stays missing is worth a warning.
            if (event.sinceStartMs >= 3000) {
              findRemoteLog(
                'warn',
                'proximity.publish.no_fix',
                { sinceStartMs: event.sinceStartMs, groupId: gid },
                { throttleKey: 'proximity.publish.no_fix' },
              );
            }
            return;
          }
          if (event.type === 'gated') {
            // Without this, a device held back by the quality gates is
            // indistinguishable in the logs from one that stopped publishing.
            findRemoteLog(
              'debug',
              'proximity.publish.gated',
              {
                reason: event.reason,
                accuracyMeters: event.accuracyMeters,
                ageMs: event.ageMs,
                groupId: gid,
              },
              { throttleKey: `proximity.publish.gated.${event.reason}` },
            );
            return;
          }
          findRemoteLog(
            'info',
            'proximity.publish.ok',
            {
              reason: event.reason,
              movedMeters: event.movedMeters,
              first: event.first,
              groupId: gid,
            },
            event.first ? { skipThrottle: true } : { throttleKey: 'proximity.publish.ok' },
          );
        },
      });

      const subscriber = createPositionSubscriber({
        client: supabase,
        userId: uid,
        peerProfiles: profiles,
        onPeerIdsChange: (peerIds) => {
          if (activeSessionRef.current !== localSession) {
            return;
          }
          engine.setRosterIds(peerIds);
          // The roster just read online: fetch this event's day keys with it, so the private beacon and its friends
          // still recognise each other once the signal is gone.
          if (beaconMode !== 'off' && eid) {
            void fetchBeaconKeys(supabase, eid).then((keys) => {
              findRemoteLog(
                'info',
                'proximity.beacon_keys',
                {
                  mode: beaconMode,
                  ok: keys !== null,
                  rows: keys?.length ?? 0,
                  people: new Set((keys ?? []).map((k) => k.userId)).size,
                },
                { skipThrottle: true },
              );
              if (keys && activeSessionRef.current === localSession) engine.setBeaconKeys(keys);
            });
          }
          findRemoteLog(
            'info',
            'proximity.roster',
            { size: peerIds.length, ids: peerIds.slice(0, 24), groupId: gid, beaconMode },
            { skipThrottle: true },
          );
        },
        onStatusTransition: ({ from, to, reason }) => {
          if (activeSessionRef.current !== localSession) {
            return;
          }
          findRemoteLog(
            'info',
            'proximity.subscription.transition',
            { from, to, reason, groupId: gid },
            { skipThrottle: true },
          );
          setSubscriptionStatus(to);
          scheduleRefresh();
        },
      });

      localSession = {
        key: keyForEffect,
        startedAt: null,
        disposed: false,
        refreshTimerId: null,
        allowEmptyPeersTimerId: null,
        heartbeatTimerId: null,
        blePaused: false,
        /** Radios suspended for backgrounding — session is alive, not disposed. */
        backgroundSuspended: false,
        compassStateSignature: null,
        engine,
        publisher,
        subscriber,
        profiles,
        locationBridge,
        selfPositionUnsub: null,
        removeListeners: null,
      };

      engineRef.current = engine;
      publisherRef.current = publisher;
      subscriberRef.current = subscriber;
      profilesRef.current = profiles;
      locationBridgeRef.current = locationBridge;
      activeSessionRef.current = localSession;

      const scheduleRefresh = () => scheduleRefreshRef.current();
      const unsubEngine = engine.subscribe(scheduleRefresh);
      const unsubRemote = onRemoteGpsPeersChange(scheduleRefresh);
      const unsubBle = onBleGpsPeersChange(scheduleRefresh);
      const unsubProfiles = profiles.onProfilesChange(scheduleRefresh);
      const unsubPeersRefresh = onFindPeersRefreshRequest((reason) => {
        if (activeSessionRef.current !== localSession) {
          return;
        }
        findRemoteLog(
          'info',
          'proximity.roster.refresh_requested',
          { reason, groupId: gid },
          { skipThrottle: true },
        );
        void subscriber.refreshPeers();
      });

      const heading = engine.getDeviceHeading();
      const syncCompassReady = () => {
        if (activeSessionRef.current !== localSession) {
          return;
        }
        const ready = heading?.isReady() ?? false;
        setCompassReady(ready);
        const needsTap = heading?.needsTap() ?? false;
        const denied = heading?.isPermissionDenied() ?? false;
        // Heading fires many times a second; only transitions carry information.
        const signature = `${ready}|${needsTap}|${denied}`;
        if (localSession.compassStateSignature === signature) {
          return;
        }
        localSession.compassStateSignature = signature;
        findRemoteLog(
          'debug',
          'proximity.compass.state',
          { ready, needsTap, denied, ...(heading?.getDiagnostics?.() ?? {}) },
          // A state transition is rare and diagnostic — throttling it is how the Red
          // Rocks run ended up with no evidence of what the compass was doing.
          { skipThrottle: true },
        );
      };
      const unsubHeading =
        heading?.subscribe((deg) => {
          if (activeSessionRef.current !== localSession) {
            return;
          }
          setDeviceHeadingDeg(deg);
          syncCompassReady();
        }) ?? (() => {});
      const unsubHeadingState =
        heading?.onStateChange(() => {
          if (activeSessionRef.current !== localSession) {
            return;
          }
          setHeadingNeedsTap(heading.needsTap());
          setHeadingDenied(heading.isPermissionDenied());
          syncCompassReady();
        }) ?? (() => {});
      if (heading && activeSessionRef.current === localSession) {
        setHeadingNeedsTap(heading.needsTap());
        setHeadingDenied(heading.isPermissionDenied());
        syncCompassReady();
      }

      localSession.removeListeners = () => {
        unsubEngine();
        unsubRemote();
        unsubBle();
        unsubProfiles();
        unsubPeersRefresh();
        unsubHeading();
        unsubHeadingState();
      };

      const startupLocationAuth = await refreshLocationAuthRef.current(locationBridge);
      findRemoteLog(
        'info',
        'proximity.gps.auth',
        { status: startupLocationAuth ?? 'unknown', groupId: gid },
        { skipThrottle: true },
      );
      if (
        cancelled ||
        keyForEffect !== managedSessionRunKeyRef.current ||
        activeSessionRef.current !== localSession
      ) {
        await disposeSessionInstance(localSession);
        clearGlobalSessionRefsRef.current(localSession);
        return;
      }

      try {
        const startupT0 = Date.now();
        findRemoteLog(
          'debug',
          'proximity.session.startup.begin',
          { sessionKey: keyForEffect, groupId: gid },
          { skipThrottle: true },
        );

        await startFindRemoteLogSession({
          sessionId: crypto.randomUUID(),
          userId: uid,
          groupId: gid,
        });
        findRemoteLog(
          'info',
          'proximity.session.start',
          { sessionKey: keyForEffect },
          {
            skipThrottle: true,
          },
        );

        await engine.start();
        findRemoteLog(
          'info',
          'proximity.session.startup.engine',
          {
            sessionKey: keyForEffect,
            ms: Date.now() - startupT0,
            engineStatus: engine.status,
          },
          { skipThrottle: true },
        );
        if (
          cancelled ||
          keyForEffect !== managedSessionRunKeyRef.current ||
          activeSessionRef.current !== localSession
        ) {
          throw new Error('session superseded');
        }

        localSession.startedAt = Date.now();

        localSession.selfPositionUnsub =
          engine.getSelfPositionModule()?.onSelfPositionChange(() => {
            if (activeSessionRef.current !== localSession) {
              return;
            }
            publisher.onPositionUpdate();
            scheduleRefresh();
          }) ?? null;
        selfPositionUnsubRef.current = localSession.selfPositionUnsub;

        // The group's members and the friends you follow back who are going to the event (141). Solo (no
        // group) still watches the latter; Swift wants a group slot only to tell one session's history from
        // the next.
        await subscriber.start({
          groupId: gid ?? null,
          groupIds: gids?.length ? [...gids] : undefined,
          eventId: eid ?? null,
        });
        // A group joined while the session was starting (engine, auth, BLE all await first): watch it now (2026-10-12 audit).
        void subscriber.setGroupIds?.(runContextRef.current.groupIds ?? []);
        publisher.start(gid ?? SOLO_SESSION_GROUP, {
          liveUntilMs: liveUntilRef.current,
          venue: venueRef.current,
        });

        findRemoteLog(
          'info',
          'proximity.session.startup.complete',
          {
            sessionKey: keyForEffect,
            ms: Date.now() - startupT0,
            engineStatus: engine.status,
            subscriptionStatus: subscriber.getStatus?.() ?? 'unknown',
            publishStatus: publisher.getStatus?.() ?? null,
          },
          { skipThrottle: true },
        );

        if (
          !cancelled &&
          keyForEffect === managedSessionRunKeyRef.current &&
          activeSessionRef.current === localSession
        ) {
          setIsRunning(true);
          allowEmptyPeersRef.current = false;
          if (allowEmptyPeersTimerRef.current !== null) {
            clearTimeout(allowEmptyPeersTimerRef.current);
          }
          allowEmptyPeersTimerRef.current = window.setTimeout(() => {
            allowEmptyPeersRef.current = true;
            allowEmptyPeersTimerRef.current = null;
            // Without this the empty-but-connected state is only observed on the next
            // engine event, so an empty group reads "Connecting…" for up to a minute.
            scheduleRefreshRef.current();
          }, 4000);
          localSession.heartbeatTimerId = window.setInterval(() => {
            if (activeSessionRef.current !== localSession) {
              return;
            }
            // Hidden: nothing to draw and the resume handler resyncs on the way back (an idle web view, 2026-10-29).
            if (document.hidden) {
              return;
            }
            // A channel that errored during a signal gap used to stay dead until the app was backgrounded once
            // (2026-10-04 audit): resync it here as well.
            if (subscriber.getStatus?.() === 'error') {
              void subscriber.resync();
            }
            refreshPeers({ snapshotSource: 'heartbeat' });
          }, FIND_PEERS_SNAPSHOT_HEARTBEAT_MS);
          scheduleRefreshRef.current();
        }

        if (
          !cancelled &&
          keyForEffect === managedSessionRunKeyRef.current &&
          activeSessionRef.current === localSession &&
          !findPageActiveRef.current
        ) {
          await localSession.engine.pauseBle();
          localSession.blePaused = true;
          findRemoteLog(
            'debug',
            'find.session.ble_paused',
            { reason: 'off_find_at_start' },
            {
              skipThrottle: true,
            },
          );
        }
      } catch (err) {
        if (!cancelled && activeSessionRef.current === localSession) {
          const message = err instanceof Error ? err.message : String(err);
          if (message !== 'session superseded') {
            setStartError(message);
            findRemoteLog('error', 'proximity.session.error', { message }, { skipThrottle: true });
          }
        }
        if (activeSessionRef.current === localSession) {
          activeSessionRef.current = null;
        }
        await disposeSessionInstance(localSession);
        clearGlobalSessionRefsRef.current(localSession);
        resetUiAfterSessionStopRef.current();
      }
    };

    sessionLifecycleRef.current = sessionLifecycleRef.current.then(run, run);

    return () => {
      cancelled = true;
      const key = keyForEffect;
      sessionLifecycleRef.current = sessionLifecycleRef.current.then(async () => {
        const session = activeSessionRef.current;
        if (session && session.key === key) {
          activeSessionRef.current = null;
          await disposeSessionInstance(session);
          clearGlobalSessionRefsRef.current(session);
        }
        resetUiAfterSessionStopRef.current();
      });
    };
  }, [managedSessionRunKey, sessionEpoch]);

  const trackedPeer = useMemo(
    () => peers.find((row) => row.peerId === trackedPeerId) ?? null,
    [peers, trackedPeerId],
  );

  const statusLabel = useMemo(() => {
    if (!userId) {
      return 'Sign in required';
    }
    if (!groupId) {
      return 'Select a group';
    }
    if (!IS_NATIVE) {
      return 'Requires Prii iOS app';
    }
    if (startError) {
      return 'Error';
    }
    if (!isRunning) {
      return 'Starting…';
    }
    if (!livePeersSynced) {
      return 'Connecting…';
    }
    if (engineStatus === 'running' && subscriptionStatus === 'connected') {
      return 'Live';
    }
    if (subscriptionStatus === 'connecting' || engineStatus === 'starting') {
      return 'Connecting…';
    }
    if (subscriptionStatus === 'error' || engineStatus === 'error') {
      return 'Connection issue';
    }
    return engineStatus;
  }, [userId, groupId, startError, isRunning, livePeersSynced, engineStatus, subscriptionStatus]);

  return {
    peers,
    peersHydration,
    engineStatus,
    subscriptionStatus,
    publishStatus,
    startError,
    isRunning,
    livePeersSynced,
    canRun: Boolean(managedSessionRunKey),
    isNative: IS_NATIVE,
    statusLabel,
    locationAuth,
    locationAuthLost,
    locationAccuracy,
    headingNeedsTap,
    headingDenied,
    deviceHeadingDeg,
    compassReady,
    bleState,
    bluetoothReady: bleState === 'poweredOn',
    trackedPeerId,
    trackedPeer,
    trackedUwbReading,
    trackPeer,
    untrackPeer,
    toggleTrackPeer,
    requestLocationWhenInUse,
    requestLocationAlways,
    backgroundLocationEnabled,
    openAppSettings,
    requestCompassPermission,
    refreshLocationAuth: () => refreshLocationAuth(),
    refreshPeers,
    // Absolute-position accessors for the festival map. Getters rather than
    // state on purpose: the session already re-renders on its own cadence for
    // the radar, and the map polls these while mounted — mirroring positions
    // into hook state would re-render every Find consumer twice per fix for
    // data only the map reads. Stable identities via the refs.
    getSelfPosition,
    getMergedGpsPeers,
  };
}
